import React from 'react';
import styled from 'styled-components';
import SGBody from 'components/Typography/SGBody';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import {Site} from 'hooks/useSiteInfo/interfaces/Site';
import SiteType from 'hooks/useSiteInfo/interfaces/SiteType';
import BrandIcons from '../components/BrandIcons';

//#region styled-components
const StyledContainer = styled.div`
  background-color: ${props => props.theme.white};
  border-radius: 0px 0px 35px 35px;
  box-shadow: 0px 4px 24px 0px #00000026;
`;

const StyledSearchSuggestion = styled.ul`
  padding-left: 0px;
  margin: 0px;
  max-height: 375px;
  overflow-y: hidden;
`;

const StyledSearchItems = styled.li`
  color: ${props => props.theme.altText};
  padding: 0px;
  list-style: none;
  margin-bottom: 0px;
  overflow: hidden;
`;

const StyledNavLink = styled(Button)`
  border-radius: 0;
  width: 100%;
  text-transform: none;
  justify-content: flex-start;

  .MuiButton-label {
    display: grid;
    font-size: 0.8rem;
    color: ${props => props.theme.subtleText};
    text-decoration: none;
    padding: 8px 8px;
    grid-column-gap: 16px;
    grid-template-areas:
      'icon siteName'
      'icon siteInfo';
    grid-template-columns: 48px auto;
    grid-template-rows: auto auto;
    text-align: left;
    transition-duration: 150ms;
  }

  &:visited .MuiButton-label {
    color: ${props => props.theme.subtleText};
    text-decoration: none;
  }
  &:hover,
  &:focus {
    background-color: ${props => props.theme.activeStateBlue}80;
  }
  &:hover .MuiButton-label,
  &:focus .MuiButton-label {
    color: ${props => props.theme.altText};
    text-decoration: none;
  }
  &.disabled {
    opacity: 0.5;
    &:hover,
    &:focus {
      background-color: ${props => props.theme.disabledStateBlue}80;
    }
    &:hover .MuiButton-label,
    &:focus .MuiButton-label {
      color: ${props => props.theme.disabledStateText};
      text-decoration: none;
    }
  }
`;

const StyledSGSiteName = styled(SGBody)`
  grid-area: siteName;
  color: ${props => props.theme.black};
  font-weight: 700;
`;

const StyledSubInfo = styled.div`
  display: flex;
  grid-area: siteInfo;
`;

const StyledSGInfo = styled(SGBody)`
  margin-right: 6px;
  color: ${props => props.theme.grey};
  font-weight: 700;
`;

const StyledDivider = styled(Divider)`
  width: 350px;
`;
const StyledIconButton = styled(Button)`
  padding: 0px 0px 0px;
  width: 200px;
  height: 52px;
  color: ${props => props.theme.black};
  &:hover {
    background-color: white;
  }
  font-weight: 700;
  text-decoration: none;
  text-transform: none;
`;
//#endregion styled-components

const siteTypeLabel: Record<string, string> = {
  DC: 'DC',
  retail: 'Retail',
  showroom: 'Showroom',
  idf: 'DC-IDF',
  corporate: 'Corporate',
};

const getSiteTypeLabel = (siteType: string) => {
  const recordKey = siteType.toLowerCase();
  return siteTypeLabel[recordKey] || siteType;
};

const siteStatusLabel: Record<string, string> = {
  preopen: 'Pre-Open',
  open: 'Open',
  closed: 'Closed',
};

const getSiteStatusLabel = (siteStatus: string) => {
  const recordKey = siteStatus.toLowerCase();
  return siteStatusLabel[recordKey] || siteStatus;
};

interface SiteIdSearchSuggestionsProps {
  suggestedSearchItems: Site[];
  clearUserInput: () => void;
  clickHandler: (site: Site) => void;
  handleViewAllClick: () => void;
}

function SiteIdSearchSuggestions({
  suggestedSearchItems,
  clearUserInput,
  clickHandler,
  handleViewAllClick,
}: SiteIdSearchSuggestionsProps) {
  const numberOfSitesToDisplay = 30;

  const handleItemClick = (site: Site) => {
    clickHandler(site);
    clearUserInput();
  };

  return (
    <StyledContainer>
      <StyledSearchSuggestion>
        {suggestedSearchItems.slice(0, numberOfSitesToDisplay).map(searchItem => {
          const hasNpos = 'hasNpos' in searchItem ? searchItem.hasNpos : false;

          return (
            <StyledSearchItems key={searchItem.siteId}>
              <StyledNavLink
                id={searchItem.siteId}
                onClick={() => handleItemClick(searchItem)}
                disabled={!hasNpos}
                className={hasNpos ? 'npos' : 'disabled'}
              >
                <BrandIcons siteBrand={searchItem.brand} color="dark" />
                <StyledSGSiteName variant={'b6'}>
                  {`${searchItem.siteCode} ${searchItem.siteName}`}
                </StyledSGSiteName>
                <StyledSubInfo>
                  <StyledSGInfo variant={'b6'}>{searchItem.brand}</StyledSGInfo>
                  <StyledSGInfo variant={'b6'}>{searchItem.geo}</StyledSGInfo>
                  <StyledSGInfo variant={'b6'}>
                    {getSiteTypeLabel(searchItem.siteType)}
                  </StyledSGInfo>
                  {searchItem.siteType === SiteType.Retail && (
                    <StyledSGInfo variant={'b6'}>
                      {getSiteStatusLabel(searchItem.businessStatus)}
                    </StyledSGInfo>
                  )}
                  {hasNpos && <StyledSGInfo variant={'b6'}>NPOS</StyledSGInfo>}
                </StyledSubInfo>
              </StyledNavLink>
            </StyledSearchItems>
          );
        })}
      </StyledSearchSuggestion>
      {suggestedSearchItems.length > 0 && (
        <>
          <Grid container alignItems="center" justify="center" direction="column">
            <Grid item>
              <StyledDivider />
            </Grid>
            <Grid item>
              <StyledIconButton onClick={handleViewAllClick}>Show all results</StyledIconButton>
            </Grid>
          </Grid>
        </>
      )}
    </StyledContainer>
  );
}

export default SiteIdSearchSuggestions;
